import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "../../../../hooks/store-hooks"
import { useLimitacaoConsumoService } from "../../../../../use-cases/limitacao-consumo"
import { useUIService } from "../../../../../use-cases/ui"
import { useNavigationService } from "../../../../../use-cases/navigation"
import { useProdutoLimitacaoConsumoApi } from "../../../../../infra/produto-limitacao-consumo"
import { AppBar, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Toolbar, Typography } from "@mui/material"
import { onlyNumbers } from "../../../../../utils"
import { ILimitacaoConsumoTable, IProdutoLimitacaoConsumoDetails, IProdutoTipoConsumoLimitacaoConsumoAtivo } from "../../../../../application/models"
import CloseIcon from '@mui/icons-material/Close'
import CategoryIcon from '@mui/icons-material/Category'
import ChecklistIcon from '@mui/icons-material/Checklist'
import LayersIcon from '@mui/icons-material/Layers'

export const FormCreateTravaConsumoUsuario = () => {
    // State
    const { createLimitacaoConsumoUsuarioForm, produtosList } = useAppSelector((state) => state.limitacaoConsumo)
    // Use State
    const [loading, setLoading] = useState<boolean>(true)
    const [travasAtivas, setTravasAtivas] = useState<ILimitacaoConsumoTable[]>([])
    const [idProdutoParaVariaveis, setIdProdutoParaVariaveis] = useState(0)
    const [variaveisDoProdutoSelecionado, setVariaveisDoProdutoSelecionado] = useState<IProdutoTipoConsumoLimitacaoConsumoAtivo[]>([])
    const [loadingVariaveis, setLoadingVariaveis] = useState(false)
    const [searchTermVariaveis, setSearchTermVariaveis] = useState("")
    const [modo, setModo] = useState<'produto' | 'variavel'>('produto')
    // Use Ref
    const isFirstRender = useRef(false)
    // Service
    const {
        setCreateLimitacaoConsumoUsuarioOpenForm,
        setCreateLimitacaoConsumoUsuarioFormIdProduto,
        setCreateLimitacaoConsumoUsuarioFormIdTipoConsumo,
        setCreateLimitacaoConsumoUsuarioFormQuantidade,
        setCreateLimitacaoConsumoUsuarioFormIsGrupo,
        createLimitacaoConsumoUsuario,
        getProdutoLimitacaoConsumoList,
        findAllActiveLimitacaoConsumoUsuario
    } = useLimitacaoConsumoService()
    const { setAlert } = useUIService()
    const { navigateTo } = useNavigationService()
    const { produtoLimitacaoConsumoGetTipoConsumoAtivosApi } = useProdutoLimitacaoConsumoApi()
    // useEffect
    useEffect(() => {
        if (!isFirstRender.current) {
            setLoading(false)
            isFirstRender.current = true

            if (!createLimitacaoConsumoUsuarioForm.fields.IdUsuario || createLimitacaoConsumoUsuarioForm.fields.IdUsuario === 0) {
                navigateTo({
                    route: '/dashboard/trava/consumo'
                })
            } else {
                void getProdutoLimitacaoConsumoList()
                void (async () => {
                    const ativas = await findAllActiveLimitacaoConsumoUsuario({
                        IdUsuario: createLimitacaoConsumoUsuarioForm.fields.IdUsuario
                    })
                    setTravasAtivas(ativas)
                })()
            }
        }
    }, [createLimitacaoConsumoUsuarioForm.fields.IdUsuario, findAllActiveLimitacaoConsumoUsuario, getProdutoLimitacaoConsumoList, navigateTo, setAlert])

    useEffect(() => {
        if (modo !== 'variavel') {
            setIdProdutoParaVariaveis(0)
            setVariaveisDoProdutoSelecionado([])
            setSearchTermVariaveis("")
            if (createLimitacaoConsumoUsuarioForm.fields.IdTipoConsumo.length > 0) {
                setCreateLimitacaoConsumoUsuarioFormIdTipoConsumo({ IdTipoConsumo: [] })
            }
        } else {
            if (createLimitacaoConsumoUsuarioForm.fields.IdProduto > 0) {
                setCreateLimitacaoConsumoUsuarioFormIdProduto({ IdProduto: 0 })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modo])

    useEffect(() => {
        if (idProdutoParaVariaveis > 0) {
            setLoadingVariaveis(true)
            void (async () => {
                try {
                    const res = await produtoLimitacaoConsumoGetTipoConsumoAtivosApi(idProdutoParaVariaveis)
                    setVariaveisDoProdutoSelecionado(res ?? [])
                } catch {
                    setVariaveisDoProdutoSelecionado([])
                } finally {
                    setLoadingVariaveis(false)
                }
            })()
        } else {
            setVariaveisDoProdutoSelecionado([])
        }
    }, [idProdutoParaVariaveis, produtoLimitacaoConsumoGetTipoConsumoAtivosApi])

    const produtoTemTravaGeral = useCallback((idProduto: number) => {
        return travasAtivas.some((t) => t.IdProduto === idProduto && (t.IdTipoConsumo == null || t.IdTipoConsumo === 0))
    }, [travasAtivas])

    const variavelTemTravaAtiva = useCallback(
        (idTipoConsumo: number) =>
            travasAtivas.some((t) => t.IdTipoConsumo != null && Number(t.IdTipoConsumo) === Number(idTipoConsumo)),
        [travasAtivas]
    )

    const alternarVariavelSelecionada = useCallback(
        (idTipoConsumo: number, checked: boolean) => {
            if (checked && variavelTemTravaAtiva(idTipoConsumo)) return
            const atual = new Set(createLimitacaoConsumoUsuarioForm.fields.IdTipoConsumo)
            if (checked) atual.add(idTipoConsumo)
            else atual.delete(idTipoConsumo)
            setCreateLimitacaoConsumoUsuarioFormIdTipoConsumo({ IdTipoConsumo: Array.from(atual) })
        },
        [createLimitacaoConsumoUsuarioForm.fields.IdTipoConsumo, setCreateLimitacaoConsumoUsuarioFormIdTipoConsumo, variavelTemTravaAtiva]
    )

    const produtoSelecionadoParaVariaveis = useMemo(() => {
        return produtosList.find((p) => p.IdProduto === idProdutoParaVariaveis) ?? null
    }, [produtosList, idProdutoParaVariaveis])

    // Function
    const handleClose = () => {
        setCreateLimitacaoConsumoUsuarioOpenForm({
            open: false
        })
    }

    return (
        loading ? <></>
            : <>
                <Dialog
                    open={true}
                    fullScreen
                    disableEscapeKeyDown
                    onClose={handleClose}
                    scroll={'paper'}
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                >
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
                                Cadastrar nova trava de consumo para o usuário
                            </Typography>
                            <Button
                                color="inherit"
                                startIcon={<CloseIcon />}
                                onClick={handleClose}
                                aria-label="close"
                            >
                                Fechar
                            </Button>
                        </Toolbar>
                    </AppBar>
                    <DialogContent dividers={true}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <TextField
                                    value={
                                        createLimitacaoConsumoUsuarioForm.fields.Quantidade === 0 ? ''
                                            : createLimitacaoConsumoUsuarioForm.fields.Quantidade.toLocaleString('pt-BR')
                                    }
                                    onChange={(e) => {
                                        setCreateLimitacaoConsumoUsuarioFormQuantidade({
                                            Quantidade: Number(onlyNumbers(e.target.value))
                                        })
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    error={Boolean(createLimitacaoConsumoUsuarioForm.touched && createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "Quantidade"))}
                                    helperText={createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "Quantidade")?.message}
                                    name="quantidade"
                                    fullWidth
                                    id="quantidade"
                                    label="Quantidade"
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary', display: "block" }}>
                                    Modalidade da Trava
                                </FormLabel>
                                <ToggleButtonGroup
                                    exclusive
                                    fullWidth
                                    value={modo}
                                    onChange={(_e, value) => {
                                        if (value != null) {
                                            setModo(value)
                                            if (value === 'produto') {
                                                setCreateLimitacaoConsumoUsuarioFormIsGrupo({ IsGrupo: false })
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="produto" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                        <CategoryIcon fontSize="small" /> Por produto
                                    </ToggleButton>
                                    <ToggleButton value="variavel" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                        <ChecklistIcon fontSize="small" /> Por variável
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            {modo === 'produto' && (
                                <Grid item xs={12}>
                                    <Autocomplete
                                        fullWidth
                                        autoComplete
                                        includeInputInList
                                        options={[...produtosList].sort((a, b) =>
                                            a.Descricao.localeCompare(b.Descricao)
                                        )}
                                        getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                        getOptionDisabled={(option) => produtoTemTravaGeral(option.IdProduto)}
                                        value={produtosList.find((produto) => produto.IdProduto === createLimitacaoConsumoUsuarioForm.fields.IdProduto) ?? null}
                                        onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                            if (newValue) {
                                                if (produtoTemTravaGeral(newValue.IdProduto)) {
                                                    setAlert({
                                                        message: "Este produto já possui uma trava de consumo geral ativa para este usuário.",
                                                        type: "error"
                                                    })
                                                    return
                                                }
                                                setCreateLimitacaoConsumoUsuarioFormIdProduto({ IdProduto: newValue.IdProduto })
                                            } else {
                                                setCreateLimitacaoConsumoUsuarioFormIdProduto({ IdProduto: 0 })
                                            }
                                        }}
                                        renderOption={(props, option) => {
                                            const temTravaGeral = produtoTemTravaGeral(option.IdProduto)
                                            return (
                                                <li {...props} style={{
                                                    ...props.style,
                                                    opacity: temTravaGeral ? 0.5 : 1,
                                                    cursor: temTravaGeral ? "not-allowed" : "pointer"
                                                }}>
                                                    {option.Descricao.toUpperCase()}
                                                    {temTravaGeral && " (Trava geral já ativa)"}
                                                </li>
                                            )
                                        }}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Selecione um produto"
                                                error={Boolean(createLimitacaoConsumoUsuarioForm.touched && createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "IdProduto"))}
                                                helperText={createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "IdProduto")?.message}
                                            />
                                        }
                                    />
                                </Grid>
                            )}
                            {modo === 'variavel' && (
                                <>
                                    <Grid item xs={12}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                            <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase" sx={{ display: 'block', mb: 1 }}>
                                                Formato da trava por variável
                                            </Typography>
                                            <ToggleButtonGroup
                                                exclusive
                                                fullWidth
                                                size="small"
                                                value={createLimitacaoConsumoUsuarioForm.fields.IsGrupo ? "grupo" : "individual"}
                                                onChange={(_e, value) => {
                                                    if (value != null) {
                                                        setCreateLimitacaoConsumoUsuarioFormIsGrupo({
                                                            IsGrupo: value === "grupo"
                                                        })
                                                    }
                                                }}
                                            >
                                                <ToggleButton value="individual" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                    <ChecklistIcon fontSize="small" /> Travas Individuais
                                                </ToggleButton>
                                                <ToggleButton value="grupo" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                    <LayersIcon fontSize="small" /> Trava em Grupo (Compartilhada)
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="bold" color="primary" textTransform="uppercase">
                                                {createLimitacaoConsumoUsuarioForm.fields.IsGrupo ? "Variáveis do Grupo (Limite Compartilhado)" : "Variáveis (Travas Separadas)"}
                                            </Typography>
                                            <Chip
                                                icon={createLimitacaoConsumoUsuarioForm.fields.IsGrupo ? <LayersIcon style={{ fontSize: '0.9rem' }} /> : <ChecklistIcon style={{ fontSize: '0.9rem' }} />}
                                                label={createLimitacaoConsumoUsuarioForm.fields.IsGrupo ? "GRUPO COMPARTILHADO" : "TRAVAS SEPARADAS"}
                                                color={createLimitacaoConsumoUsuarioForm.fields.IsGrupo ? "primary" : "default"}
                                                size="small"
                                                sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                            />
                                        </Stack>
                                        <Autocomplete
                                            fullWidth
                                            autoComplete
                                            includeInputInList
                                            options={[...produtosList].sort((a, b) =>
                                                a.Descricao.localeCompare(b.Descricao)
                                            )}
                                            getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                            value={produtoSelecionadoParaVariaveis}
                                            onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                                const id = newValue?.IdProduto ?? 0
                                                setIdProdutoParaVariaveis(id)
                                                setSearchTermVariaveis("")
                                                setCreateLimitacaoConsumoUsuarioFormIdProduto({ IdProduto: id })
                                                setCreateLimitacaoConsumoUsuarioFormIdTipoConsumo({ IdTipoConsumo: [] })
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Selecione o produto para listar as variáveis"
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {Boolean(createLimitacaoConsumoUsuarioForm.touched && createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "IdTipoConsumo")) && (
                                            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                                                {createLimitacaoConsumoUsuarioForm.errors.find(item => item.key === "IdTipoConsumo")?.message}
                                            </Typography>
                                        )}
                                        {idProdutoParaVariaveis > 0 && loadingVariaveis && (
                                            <Typography variant="body2" color="text.secondary">Carregando variáveis...</Typography>
                                        )}
                                        {idProdutoParaVariaveis > 0 && !loadingVariaveis && variaveisDoProdutoSelecionado.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">Nenhuma variável disponível para este produto.</Typography>
                                        )}
                                        {idProdutoParaVariaveis > 0 && !loadingVariaveis && variaveisDoProdutoSelecionado.length > 0 && (
                                            <FormControl component="fieldset" fullWidth>
                                                <FormLabel component="legend" sx={{ fontSize: "0.9rem", mb: 1 }}>
                                                    Escolha as variáveis
                                                </FormLabel>
                                                <TextField
                                                    size="small"
                                                    placeholder="Pesquisar variável..."
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ mb: 1 }}
                                                    value={searchTermVariaveis}
                                                    onChange={(e) => setSearchTermVariaveis(e.target.value)}
                                                />
                                                <FormGroup sx={{ pl: 2 }}>
                                                    {variaveisDoProdutoSelecionado
                                                        .filter((tc) =>
                                                            !searchTermVariaveis ||
                                                            tc.Descricao.toLowerCase().includes(searchTermVariaveis.toLowerCase())
                                                        )
                                                        .map((variavel) => {
                                                            const bloqueada = variavelTemTravaAtiva(variavel.IdTipoConsumo)
                                                            return (
                                                                <FormControlLabel
                                                                    key={variavel.IdTipoConsumo}
                                                                    disabled={bloqueada}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={createLimitacaoConsumoUsuarioForm.fields.IdTipoConsumo.some((id) => Number(id) === Number(variavel.IdTipoConsumo))}
                                                                            disabled={bloqueada}
                                                                            onChange={(event) => {
                                                                                alternarVariavelSelecionada(variavel.IdTipoConsumo, event.target.checked)
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={`${variavel.Descricao}${bloqueada ? " (já possui trava ativa)" : ""}`}
                                                                />
                                                            )
                                                        })}
                                                </FormGroup>
                                            </FormControl>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <Button onClick={createLimitacaoConsumoUsuario} variant="contained">Criar trava de consumo</Button>
                    </DialogActions>
                </Dialog>
            </>
    )
}